/**
 * @file ContractEndDateSectionContainer.tsx
 * @description Logic owner for the dedicated Contract End Date section
 *   (SE-4881). Holds the inline-edit toggle + draft state, formats the
 *   literal `YYYY-MM-DD` `endDate` for display (e.g. "30 Jun 2027"),
 *   and wires the save action to `useEditContractEndDate`. The section
 *   is rendered FIRST inside the shared extraction view (short-circuited
 *   from the category loop by `ContractExtractionViewView`).
 *
 *   The end date is a literal date string, NOT a timestamp: it is
 *   parsed and formatted in UTC so a value like `2027-06-30` renders as
 *   "30 Jun 2027" for every user regardless of device timezone — the
 *   same anti-drift discipline used by the overview table's date
 *   formatter (and the reason SE-4881 introduced a dedicated string
 *   field instead of reusing a `Date`-typed value; see SE-4709).
 * @module features/contracts/components/ContractEndDateSection/ContractEndDateSectionContainer
 */

import React, { memo, useCallback, useMemo, useState } from "react";

import { CONTRACT_END_DATE_EMPTY_MESSAGE } from "@/features/contracts/constants";
import { useEditContractEndDate } from "@/features/contracts/hooks/useEditContractEndDate";
import { resolveContractEndDateSaveOutcome } from "@/features/contracts/utils/resolveContractEndDateSaveOutcome";
import { useLocaleFormat } from "@/i18n";

import ContractEndDateSectionView from "./ContractEndDateSectionView";

/** Props for {@link ContractEndDateSectionContainer}. */
export interface ContractEndDateSectionContainerProps {
  /** UUID of the extraction whose end date this section edits. */
  readonly extractionId: string;
  /** Resolved player UUID — forwarded to the mutation refetch. */
  readonly playerId: string;
  /** Document type — forwarded to the mutation refetch. */
  readonly documentType: string;
  /** Current end date as a literal `YYYY-MM-DD` string, or null. */
  readonly endDate: string | null;
  /**
   * Section heading label. Defaults to "Contract End Date" (AC1). The
   * shared extraction view threads the value it already computed in
   * `parseCategorySummaries` (`category.displayName`) so the label stays
   * in lockstep with the category formatter.
   */
  readonly label?: string;
  /** When true, the section is display-only (no pencil / no inline edit). */
  readonly readOnly: boolean;
}

/**
 * Default section heading when a caller does not thread an explicit
 * label (AC1: the section must be labelled "Contract End Date").
 */
const DEFAULT_LABEL = "Contract End Date";

/** Strict `YYYY-MM-DD` shape gate. */
const YMD_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/** User-facing copy when the draft is not a valid `YYYY-MM-DD` date. */
const INVALID_DATE_MESSAGE = "Enter a valid date in YYYY-MM-DD format.";

/**
 * Validates that a string is a real calendar date in `YYYY-MM-DD` form.
 * Rejects malformed shapes (the backend now throws
 * `BadRequestException`) AND impossible dates like `2027-13-40` by
 * round-tripping the parsed UTC date back to an ISO date string and
 * comparing — `new Date("2027-13-40")` would otherwise roll over rather
 * than fail.
 * @param value - Trimmed candidate string
 * @returns True when `value` is a valid `YYYY-MM-DD` calendar date
 */
function isValidYmd(value: string): boolean {
  if (!YMD_PATTERN.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed.toISOString().slice(0, 10) === value;
}

/**
 * Timezone-stable options for the contract end date. Pinned to UTC so a
 * literal `YYYY-MM-DD` value (parsed as UTC midnight) renders as the
 * same calendar day everywhere.
 *
 * SE-7966: the locale was hardcoded `"en-GB"` here, chosen for the
 * day-month-year order the design calls for ("30 Jun 2027"). That is the
 * right ORDER for a British reader and the wrong one for an American
 * account, and neither of them asked for it — the account language did.
 * Day/month/year ORDER is exactly what a locale decides, so the field
 * order below is now requested semantically (`day`/`month`/`year` parts)
 * and the locale arranges them.
 */
const END_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  timeZone: "UTC",
  day: "numeric",
  month: "short",
  year: "numeric",
};

/**
 * Container for the Contract End Date section.
 * @param props - Container props
 * @param props.extractionId - Extraction UUID
 * @param props.playerId - Resolved player UUID
 * @param props.documentType - Document type
 * @param props.endDate - Current literal end date or null
 * @param props.label - Section heading (defaults to "Contract End Date")
 * @param props.readOnly - Suppresses every editing affordance when true
 * @returns The rendered section
 */
function ContractEndDateSectionContainer({
  extractionId,
  playerId,
  documentType,
  endDate,
  label = DEFAULT_LABEL,
  readOnly,
}: ContractEndDateSectionContainerProps) {
  const { editContractEndDate, isEditing: isSaving } = useEditContractEndDate();

  const [isEditing, setIsEditing] = useState(false);
  const [draftText, setDraftText] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { formatDate } = useLocaleFormat();

  /**
   * The end date for display, or null when the value is absent or
   * unparseable (callers render the empty state in that case).
   */
  const displayDate = useMemo(
    () => (endDate ? formatDate(endDate, END_DATE_OPTIONS) || null : null),
    [endDate, formatDate]
  );

  const handleStartEdit = useCallback(() => {
    // Seed the draft with the raw stored value so the user edits the
    // canonical YYYY-MM-DD string, not the localized display form.
    setDraftText(endDate ?? "");
    setErrorMessage(null);
    setIsEditing(true);
  }, [endDate]);

  const handleChangeDraft = useCallback((text: string) => {
    setDraftText(text);
    // Clear a stale validation/save error as soon as the user edits.
    setErrorMessage(null);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setErrorMessage(null);
    setIsEditing(false);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    const trimmed = draftText.trim();
    // Empty input clears the end date (null). A non-empty value MUST be
    // a valid YYYY-MM-DD calendar date — the backend rejects malformed
    // dates with a BadRequestException, so we gate client-side first and
    // keep the user in edit mode with inline feedback.
    if (trimmed !== "" && !isValidYmd(trimmed)) {
      setErrorMessage(INVALID_DATE_MESSAGE);
      return;
    }
    const ok = await editContractEndDate(
      extractionId,
      trimmed === "" ? null : trimmed,
      playerId,
      documentType
    );
    // Map the mutation result to the next edit/error state via the pure
    // helper (unit-tested in isolation) so this async branch stays a
    // thin dispatch — success exits edit mode and clears the error,
    // failure stays in edit mode and surfaces the save error.
    const outcome = resolveContractEndDateSaveOutcome(ok);
    setIsEditing(outcome.isEditing);
    setErrorMessage(outcome.errorMessage);
  }, [draftText, editContractEndDate, extractionId, playerId, documentType]);

  return (
    <ContractEndDateSectionView
      label={label}
      displayDate={displayDate}
      emptyMessage={CONTRACT_END_DATE_EMPTY_MESSAGE}
      isEditable={isEditing && !readOnly}
      isSaving={isSaving}
      draftText={draftText}
      errorMessage={errorMessage}
      readOnly={readOnly}
      onStartEdit={handleStartEdit}
      onChangeDraft={handleChangeDraft}
      onSaveEdit={handleSaveEdit}
      onCancelEdit={handleCancelEdit}
    />
  );
}

ContractEndDateSectionContainer.displayName = "ContractEndDateSectionContainer";

export default memo(ContractEndDateSectionContainer);
